/**
 * Debug-only utility exposed as `window.domJSON` so support can serialize a
 * snapshot of the page DOM (or restore one) from the browser console on a
 * stuck kiosk. Not used at runtime by application code.
 *
 * Ported from the legacy `domJSON.js` library. The recursive node walks
 * lean on `any` deliberately: every property access is dynamic, and forcing
 * strict DOM types here either rejects valid input or hides fallback
 * behavior the original relied on.
 */

type Json = unknown;
type AnyRecord = Record<string, any>;

interface Metadata {
  href: string | null;
  userAgent: string | null;
  version: string;
}

const metadata: Metadata = {
  href: (typeof window !== 'undefined' && window.location?.href) || null,
  userAgent:
    typeof window !== 'undefined' && window.navigator?.userAgent
      ? window.navigator.userAgent
      : null,
  version: '0.1.2',
};

export interface ToJsonOptions {
  absolutePaths?: unknown;
  attributes?: unknown;
  computedStyle?: unknown;
  cull?: boolean;
  deep?: boolean | number;
  domProperties?: unknown;
  filter?: unknown;
  htmlOnly?: boolean;
  metadata?: boolean;
  serialProperties?: unknown;
  stringify?: boolean;
  dataset?: unknown;
  allowDangerousElements?: unknown;
}

export interface ToDomOptions {
  noMeta?: boolean;
  allowDangerousElements?: unknown;
}

const defaultsForToJSON: ToJsonOptions = {
  absolutePaths: ['action', 'data', 'href', 'src'],
  attributes: true,
  computedStyle: false,
  cull: true,
  deep: true,
  domProperties: true,
  filter: false,
  htmlOnly: false,
  metadata: true,
  serialProperties: false,
  stringify: false,
};

const defaultsForToDOM: ToDomOptions = { noMeta: false };

const BANNED_PROPS = new Set(['link', 'script']);
const REQUIRED_PROPS = ['nodeType', 'nodeValue', 'tagName'];
const IGNORED_PROPS = new Set([
  'attributes',
  'childNodes',
  'children',
  'classList',
  'dataset',
  'style',
]);
const SERIAL_PROPS = [
  'innerHTML',
  'innerText',
  'outerHTML',
  'outerText',
  'prefix',
  'text',
  'textContent',
  'wholeText',
];

const EXCLUDED_PROP_LOOKUP = new Set<string>([
  ...BANNED_PROPS,
  ...IGNORED_PROPS,
  ...SERIAL_PROPS,
]);

const ABSOLUTE_URL_RE = /(?:^[a-z][a-z0-9+.-]*:)|(?:^\/\/)/i;
const MAX_COPY_DEPTH = 100;

function unique<T>(...arrays: T[][]): T[] {
  return Array.from(new Set(arrays.flat()));
}

interface BoolDiffOptions {
  reverse?: boolean;
  boolTransform?: (item: any) => any;
}

function boolDiff(a: any[], b: any[], opts: BoolDiffOptions = {}): any[] {
  const { reverse = false, boolTransform = (item) => item } = opts;
  const [source, compare] = reverse ? [a, b] : [b, a];
  const compareSet = new Set(compare);
  return source.filter((item) => !compareSet.has(boolTransform(item)));
}

const keepOrNull = <T>(items: T[]): T[] | null =>
  items.length > 0 ? items : null;

function boolFilter(list: any[], filter: any): any[] | null {
  if (filter === false) return null;
  if (filter === true) return list;

  if (Array.isArray(filter) && filter.length > 0) {
    if (typeof filter[0] === 'boolean') {
      if (filter.length === 1) return filter[0] ? list : null;
      const rest = filter.slice(1);
      if (filter[0]) return boolDiff(list, rest);
      return keepOrNull(list.filter((item) => rest.includes(item)));
    }
    return keepOrNull(list.filter((item) => filter.includes(item)));
  }

  if (filter && typeof filter === 'object') {
    if (Array.isArray(filter.values)) {
      if (filter.exclude) return boolDiff(list, filter.values);
      return keepOrNull(list.filter((item) => filter.values.includes(item)));
    }
    return list;
  }
  return list;
}

function toShorthand(filterList: any): boolean | any[] | undefined {
  if (typeof filterList === 'boolean') return filterList;
  if (Array.isArray(filterList)) return filterList;
  if (!filterList || typeof filterList !== 'object') return undefined;

  const out: any[] = [];
  if (filterList.exclude) out.push(true);
  if (Array.isArray(filterList.values) && filterList.values.length > 0) {
    out.push(...filterList.values);
  }
  return out;
}

function copyPrimitiveProps(node: any, opts: AnyRecord, copy: AnyRecord): void {
  let propList: any[] | null;
  if (opts.domProperties) {
    propList = Object.keys(node).filter(
      (p) => !EXCLUDED_PROP_LOOKUP.has(p.toLowerCase())
    );
    propList = boolFilter(propList, opts.domProperties);
  } else {
    propList = boolFilter(REQUIRED_PROPS, opts.domProperties);
  }

  if (propList) {
    for (const name of propList) {
      const value = node[name];
      if (
        value !== undefined &&
        value !== null &&
        typeof value !== 'object' &&
        typeof value !== 'function'
      ) {
        copy[name] = value;
      }
    }
  }

  if (opts.serialProperties) {
    const serialList = boolFilter(SERIAL_PROPS, opts.serialProperties);
    if (serialList) {
      for (const name of serialList) {
        if (node[name]) copy[name] = node[name];
      }
    }
  }
}

function copyAttributes(
  node: any,
  opts: AnyRecord,
  copy: AnyRecord,
  attrList: any[]
): void {
  copy.attributes = {};
  for (const name of attrList) {
    copy.attributes[name] = node.attributes[name].value;
  }

  const absAttr = boolFilter(attrList, opts.absolutePaths);
  if (!absAttr) return;

  const baseUrl =
    typeof window !== 'undefined' && window.location
      ? `${window.location.protocol}//${window.location.host}`
      : '';

  for (const name of absAttr) {
    const href = node.attributes[name].value as string | undefined;
    if (!href || href.charAt(0) === '#' || ABSOLUTE_URL_RE.test(href)) continue;
    const prefix = href.charAt(0) === '/' ? '' : '/';
    copy.attributes[name] = `${baseUrl}${prefix}${href}`;
  }
}

function copyComputedStyle(
  node: Element,
  opts: AnyRecord,
  copy: AnyRecord
): void {
  if (typeof window === 'undefined' || !window.getComputedStyle) return;

  const style = window.getComputedStyle(node);
  if (!style?.length) return;

  const all = Array.from(style) as string[];
  const list = boolFilter(all, opts.computedStyle);
  if (!list) return;

  copy.computedStyle = {};
  for (const name of list) {
    copy.computedStyle[name] = style.getPropertyValue(name);
  }
}

function copyChildren(
  node: any,
  opts: AnyRecord,
  copy: AnyRecord,
  depth: number
): void {
  // Allocate the next-depth `opts` shape once per parent, not per child: the
  // only field that differs between parent and child is `deep`, and every
  // child in this loop gets the same value. Avoids ~N object clones on a
  // wide DOM tree.
  const nextDeep = typeof opts.deep === 'number' ? opts.deep - 1 : opts.deep;
  if (nextDeep === false || nextDeep === 0) return;
  const childOpts: AnyRecord = { ...opts, deep: nextDeep };

  const kids: any[] = [];
  for (const child of node.childNodes as Iterable<any>) {
    if (opts.htmlOnly && child.nodeType !== 1) continue;
    const kid = copyJSON(child, childOpts, depth + 1);
    if (kid) kids.push(kid);
  }
  if (kids.length > 0) copy.childNodes = kids;
}

function copyDataset(node: any, opts: AnyRecord, copy: AnyRecord): void {
  const list = boolFilter(Object.keys(node.dataset), opts.dataset);
  if (!list || list.length === 0) return;
  copy.dataset = {};
  for (const name of list) {
    copy.dataset[name] = node.dataset[name];
  }
}

function copyJSON(node: any, opts: AnyRecord, depth = 0): AnyRecord | null {
  if (depth > MAX_COPY_DEPTH) {
    return { nodeType: node.nodeType, _truncated: 'MAX_DEPTH_EXCEEDED' };
  }

  const tagOrName =
    node.nodeType === 1 ? node.tagName.toLowerCase() : node.nodeName;
  if (!boolFilter([tagOrName], opts.allowDangerousElements)) return null;

  const copy: AnyRecord = {};

  const hasAttrs = !!node.attributes && node.attributes.length > 0;
  const hasChildren = !!node.childNodes && node.childNodes.length > 0;
  const hasDataset = !!node.dataset && Object.keys(node.dataset).length > 0;

  const attrsAllowed = opts.cull
    ? hasAttrs &&
      !!boolFilter(unique(opts.attributes, opts.absolutePaths), false)
    : hasAttrs;

  copyPrimitiveProps(node, opts, copy);

  if (attrsAllowed) {
    const attrNames = Array.from(
      node.attributes as Iterable<{ name: string }>,
      (a) => a.name
    );
    const attrList = opts.attributes
      ? boolFilter(attrNames, opts.attributes)
      : attrNames;
    if (attrList) copyAttributes(node, opts, copy, attrList);
  }

  if (opts.computedStyle && node.nodeType === 1) {
    copyComputedStyle(node as Element, opts, copy);
  }

  if (hasChildren && opts.deep) copyChildren(node, opts, copy, depth);
  if (hasDataset) copyDataset(node, opts, copy);

  return copy;
}

function toJSON(node: any, opts?: ToJsonOptions): Json {
  if (!node?.nodeType) return null;

  const settings: AnyRecord = { ...defaultsForToJSON, ...opts };
  settings.absolutePaths = toShorthand(settings.absolutePaths);
  settings.attributes = toShorthand(settings.attributes);
  settings.computedStyle = toShorthand(settings.computedStyle);
  settings.dataset = toShorthand(settings.dataset);
  settings.domProperties = toShorthand(settings.domProperties);
  settings.serialProperties = toShorthand(settings.serialProperties);
  settings.allowDangerousElements = toShorthand(
    settings.allowDangerousElements
  );

  const copy = copyJSON(node, settings);
  const wrapped: AnyRecord | null =
    settings.metadata && copy ? { meta: metadata, node: copy } : copy;

  return settings.stringify ? JSON.stringify(wrapped) : wrapped;
}

function createNode(
  type: number,
  doc: Document | DocumentFragment,
  data: any
): Node | false {
  const resolvedDoc: Document =
    doc instanceof DocumentFragment ? doc.ownerDocument || document : doc;

  switch (type) {
    case 1: // Element
      return typeof data.tagName === 'string'
        ? resolvedDoc.createElement(data.tagName)
        : false;
    case 3: // Text
      return resolvedDoc.createTextNode(
        typeof data.nodeValue === 'string' ? data.nodeValue : ''
      );
    case 7: // ProcessingInstruction
      return Object.hasOwn(data, 'target') && Object.hasOwn(data, 'data')
        ? resolvedDoc.createProcessingInstruction(data.target, data.data)
        : false;
    case 8: // Comment
      return resolvedDoc.createComment(
        typeof data.nodeValue === 'string' ? data.nodeValue : ''
      );
    case 9: // Document
      return (resolvedDoc.implementation as any).createHTMLDocument(data);
    case 10: // DocumentType
      return resolvedDoc.implementation.createDocumentType(
        data.name || 'html',
        data.publicId || '',
        data.systemId || ''
      );
    case 11: // DocumentFragment
      return resolvedDoc.createDocumentFragment();
    default:
      return false;
  }
}

const TODO_SKIP_KEYS = new Set([
  'isContentEditable',
  'childNodes',
  'attributes',
  'nodeType',
  'tagName',
  'nodeName',
]);

function toDOM(
  obj: any,
  parent: Node | null,
  doc: Document,
  opts: any
): Node | false {
  if (!obj?.nodeType) return false;

  if (obj.nodeType === 1 && obj.tagName) {
    const allowed = boolFilter(
      [obj.tagName.toLowerCase()],
      opts.allowDangerousElements
    );
    if (allowed && allowed.length === 0) return false;
  }

  const node = createNode(obj.nodeType, doc, obj);
  if (!node) return false;

  const assignFailures: string[] = [];
  for (const x in obj) {
    if (TODO_SKIP_KEYS.has(x) || typeof obj[x] === 'object') continue;
    try {
      (node as any)[x] = obj[x];
    } catch {
      // Read-only DOM props legitimately throw — collect names, warn once.
      assignFailures.push(x);
    }
  }
  if (assignFailures.length > 0) {
    console.warn(
      '[DOM] Read-only property assignments skipped:',
      assignFailures
    );
  }

  // Skip on* attributes — restoring untrusted JSON must not let the snapshot
  // wire up event handlers (XSS via onclick/onerror).
  if (obj.nodeType === 1 && obj.tagName && obj.attributes) {
    for (const a of Object.keys(obj.attributes)) {
      if (a.toLowerCase().startsWith('on')) continue;
      (node as Element).setAttribute(a, obj.attributes[a]);
    }
  }

  if (obj.childNodes?.length > 0) {
    for (const child of obj.childNodes) {
      const childNode = toDOM(child, node, doc, opts);
      if (childNode) node.appendChild(childNode);
    }
  }

  if (parent) parent.appendChild(node);
  return node;
}

function toDOMPublic(obj: any, opts?: ToDomOptions): DocumentFragment {
  const settings: AnyRecord = { ...defaultsForToDOM, ...opts };
  settings.allowDangerousElements = toShorthand(
    settings.allowDangerousElements
  );

  let parsedObj: any;
  if (typeof obj === 'string') {
    try {
      parsedObj = JSON.parse(obj);
    } catch (e) {
      console.warn(
        '[domJSON] Failed to parse JSON string in toDOM:',
        (e as Error).message
      );
      return document.createDocumentFragment();
    }
  } else {
    parsedObj = obj;
  }

  const docFragment = document.createDocumentFragment();
  const nodeData = settings.noMeta ? parsedObj : parsedObj?.node;
  if (!nodeData) return docFragment;

  if (nodeData.nodeType === 11 && nodeData.childNodes) {
    for (const child of nodeData.childNodes) {
      toDOM(child, docFragment, document, settings);
    }
  } else {
    toDOM(nodeData, docFragment, document, settings);
  }

  return docFragment;
}

interface DomJSON {
  toJSON: typeof toJSON;
  toDOM: typeof toDOMPublic;
}

const domJSON: DomJSON = { toJSON, toDOM: toDOMPublic };

export default domJSON;
